import { useState, useEffect, useCallback, useRef } from "react";
import { listAuditLogs } from "../../../../services/platform_admin_service";

const PAGE_SIZE = 20;

const usePlatformAudit = () => {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, pageSize: PAGE_SIZE, totalItems: 0, totalPages: 0 });
    const [filters, setFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const loadedPagesRef = useRef(new Set());

    const fetchPage = useCallback(async (page, currentFilters, reset = false) => {
        // Se já carregou essa página e não é reset, ignora
        if (!reset && loadedPagesRef.current.has(page)) return;

        try {
            if (page === 1 || reset) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            setError(null);

            const result = await listAuditLogs({ page, pageSize: PAGE_SIZE, filters: currentFilters });
            const newItems = result.items || [];
            const pag = result.pagination || {};

            if (reset || page === 1) {
                setItems(newItems);
                loadedPagesRef.current = new Set([page]);
            } else {
                setItems(prev => [...prev, ...newItems]);
                loadedPagesRef.current.add(page);
            }

            setPagination(pag);
        } catch (err) {
            console.error("[usePlatformAudit]", err);
            setError(err.message || "Erro ao carregar audit logs.");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    // Carga inicial
    useEffect(() => {
        loadedPagesRef.current = new Set();
        fetchPage(1, filters, true);
    }, [fetchPage, filters]);

    // Chamado pela NeoTable quando o usuario avança pagina e precisa de mais dados
    const loadMoreIfNeeded = useCallback((tableCurrentPage, tableItemsPerPage) => {
        // Quantos itens a NeoTable precisaria exibir até a página atual
        const neededItems = tableCurrentPage * tableItemsPerPage;
        // Se já temos itens suficientes, não busca mais
        if (neededItems <= items.length) return;
        // Calcula qual página server-side buscar
        const nextServerPage = Math.floor(items.length / PAGE_SIZE) + 1;
        // Se ainda há páginas no server
        if (pagination.totalPages && nextServerPage <= pagination.totalPages) {
            fetchPage(nextServerPage, filters);
        }
    }, [items.length, pagination.totalPages, fetchPage, filters]);

    const applyFilters = (newFilters) => {
        setFilters(newFilters);
    };

    const hasMore = pagination.totalPages > 0 && loadedPagesRef.current.size < pagination.totalPages;

    return {
        items,
        pagination,
        loading,
        loadingMore,
        error,
        applyFilters,
        loadMoreIfNeeded,
        hasMore,
        filters,
        totalItems: pagination.totalItems || 0
    };
};

export default usePlatformAudit;
